import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/apiService";
import analyticsService from "../services/analyticsService";
import { useFeaturedCars } from "../hooks/useFeaturedCars";
import styles from "./Signup.module.css";
import logo from "./assets/trustauto-logo.png";
import carImage1 from "./assets/car_image_1.svg";
import carImage2 from "./assets/car_image_2.svg";

const Signup = () => {
  // Show/hide password states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);

  // OTP states
  const [otpStage, setOtpStage] = useState(false);
  const [dealerId, setDealerId] = useState(null);
  const [otpData, setOtpData] = useState({ email_otp: "", mobile_otp: "" });
  const [verifying, setVerifying] = useState(false);

  const { images } = useFeaturedCars();
  const navigate = useNavigate();

  // check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      return navigate("/", { replace: true });
    }

    // set the form data from localstorage if available
    const name = localStorage.getItem("name") || "";
    const phone = localStorage.getItem("phone") || "";
    const email = localStorage.getItem("email") || "";
    setFormData((prev) => ({
      ...prev,
      name,
      phone,
      email,
    }));
  }, [navigate]);

  // Automatic image sliding
  useEffect(() => {
    if (!isAutoSliding) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [isAutoSliding, images.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // set the values to localstorage (except password fields)
    if (name !== "password" && name !== "confirmPassword") {
      localStorage.setItem(name, value);
    }
    setError("");
  };

  const handleOtpChange = (e) => {
    const { name, value } = e.target;
    setOtpData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return false;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      setError("Please enter a valid mobile number");
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }
    if (!formData.confirmPassword || formData.confirmPassword.length < 6) {
      setError("Please confirm your password (min. 6 characters)");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }
    return true;
  };

  // === STEP 1: Register & send OTPs ===
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await apiService.initiateDealerSignup(formData);
      if (!response || !response.data?.dealerId)
        throw new Error("Registration failed. Please try again.");

      const dealer_id = response.data.dealerId;
      setDealerId(dealer_id);
      localStorage.setItem("dealerId", dealer_id);
      setOtpStage(true); // move to OTP verification stage
    } catch (err) {
      console.error("Signup error:", err);
      setError(
        err?.response?.data?.message || "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // === STEP 2: Verify OTPs ===
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");

    if (!otpData.email_otp.trim() || !otpData.mobile_otp.trim()) {
      setError("Please enter both OTPs.");
      return;
    }

    setVerifying(true);
    try {
      const payload = {
        dealerId: dealerId,
        emailOtp: otpData.email_otp.trim(),
        mobileOtp: otpData.mobile_otp.trim(),
        password: formData.password,
      };

      const res = await apiService.verifyDealerSignup(payload);
      if (!res || res.data?.error) throw new Error("OTP verification failed.");


      const loginResponse = await apiService.login({
        email: formData.email,
        password: formData.password,
      });

      const { token, user } = loginResponse.data;

      localStorage.setItem("authToken", token);

      // remove the signup form data from localstorage
      localStorage.removeItem("name");
      localStorage.removeItem("phone");
      localStorage.removeItem("email");

      analyticsService.setUser({
        uid: user.uid,
        email: user.email,
        displayName: user.name || user.email,
        roles: user.roles || ["orguserdealer"],
        organization: user.organization,
        dealerId: user.uid,
        status: user.status || "NA",
      });

      // navigate("/");

      window.dispatchEvent(new Event("userDataUpdated"));
      window.location.reload();
    } catch (err) {
      console.error("OTP verification error:", err);
      setError(
        err?.response?.data?.message ||
          "Something went wrong. Please try after sometime."
      );
    } finally {
      setVerifying(false);
    }
  };

  const handleOtpBoxChange = (e, index, type) => {
    const value = e.target.value.replace(/\D/, ""); // only digits
    const otpArray = otpData[type].split("");
    otpArray[index] = value;
    const newOtp = otpArray.join("");
    setOtpData((prev) => ({ ...prev, [type]: newOtp }));

    // auto-focus next box
    if (value && e.target.nextSibling) {
      e.target.nextSibling.focus();
    }
  };

  const handleOtpBoxKeyDown = (e, index, type) => {
    if (e.key === "Backspace" && !e.target.value && e.target.previousSibling) {
      e.target.previousSibling.focus();
    }
  };

  const handlePrevImage = () => {
    setIsAutoSliding(false);
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setTimeout(() => setIsAutoSliding(true), 5000);
  };

  const handleNextImage = () => {
    setIsAutoSliding(false);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAutoSliding(true), 5000);
  };

  const handleDotClick = (index) => {
    setIsAutoSliding(false);
    setCurrentImageIndex(index);
    setTimeout(() => setIsAutoSliding(true), 5000);
  };

  return (
    <div className="container">
      <div className={styles.signup}>
        <div className={styles.container}>
          {/* LEFT PART */}
          <div className={styles.leftPart}>
            <div className={styles.leftPartContent}>
              <img src={logo} alt="Trust Auto Logo" className={styles.logo} />

              {!otpStage ? (
                <>
                  <div className={styles.welcomeMsg}>
                    <h1>Register</h1>
                    <p>Register now to access Trust Auction Platform</p>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className={styles.inputs}>
                      <label className={styles.label}>Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter your name"
                        value={formData.name}
                        onChange={handleChange}
                        className={styles.input}
                        required
                      />

                      <label className={styles.label}>Mobile Number</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+91 99999 55555"
                        value={formData.phone}
                        onChange={handleChange}
                        className={styles.input}
                        required
                      />

                      <label className={styles.label}>Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="example@trustauto.in"
                        value={formData.email}
                        onChange={handleChange}
                        className={styles.input}
                        required
                      />

                      <label className={styles.label}>Password</label>
                      <div style={{ position: "relative", width: "100%" }}>
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Min. 6 characters"
                          value={formData.password}
                          onChange={handleChange}
                          className={styles.input}
                          required
                          style={{ width: "100%" }}
                        />
                        <span
                          onClick={() => setShowPassword((prev) => !prev)}
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#888",
                            display: "flex",
                            alignItems: "center",
                            height: "100%"
                          }}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          tabIndex={0}
                          role="button"
                        >
                          {!showPassword ? (
                            // Eye Off SVG
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2 2L18 18" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                              <path d="M10 5C6 5 2.73 7.61 1.46 10.5C2.73 13.39 6 16 10 16C11.61 16 13.09 15.61 14.36 14.97M16.54 13.5C17.27 12.39 18 10.5 18 10.5C16.73 7.61 13.46 5 10 5" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                              <circle cx="10" cy="10.5" r="2.5" stroke="#888" strokeWidth="1.5"/>
                            </svg>
                          ) : (
                            // Eye SVG
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.46 10.5C2.73 7.61 6 5 10 5C14 5 17.27 7.61 18.54 10.5C17.27 13.39 14 16 10 16C6 16 2.73 13.39 1.46 10.5Z" stroke="#888" strokeWidth="1.5"/>
                              <circle cx="10" cy="10.5" r="2.5" stroke="#888" strokeWidth="1.5"/>
                            </svg>
                          )}
                        </span>
                      </div>
                      <label className={styles.label}>Confirm Password</label>
                      <div style={{ position: "relative", width: "100%" }}>
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          placeholder="Re-enter your password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={styles.input}
                          required
                          style={{ width: "100%" }}
                        />
                        <span
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#888",
                            display: "flex",
                            alignItems: "center",
                            height: "100%"
                          }}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                          tabIndex={0}
                          role="button"
                        >
                          {!showConfirmPassword ? (
                            // Eye Off SVG
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2 2L18 18" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                              <path d="M10 5C6 5 2.73 7.61 1.46 10.5C2.73 13.39 6 16 10 16C11.61 16 13.09 15.61 14.36 14.97M16.54 13.5C17.27 12.39 18 10.5 18 10.5C16.73 7.61 13.46 5 10 5" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
                              <circle cx="10" cy="10.5" r="2.5" stroke="#888" strokeWidth="1.5"/>
                            </svg>
                          ) : (
                            // Eye SVG
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1.46 10.5C2.73 7.61 6 5 10 5C14 5 17.27 7.61 18.54 10.5C17.27 13.39 14 16 10 16C6 16 2.73 13.39 1.46 10.5Z" stroke="#888" strokeWidth="1.5"/>
                              <circle cx="10" cy="10.5" r="2.5" stroke="#888" strokeWidth="1.5"/>
                            </svg>
                          )}
                        </span>
                      </div>
                    </div>

                    <p className={styles.otpMsg}>
                      We will send an OTP to your Email address and Mobile
                      Number
                    </p>

                    {error && <p className={styles.errorMessage}>⚠️ {error}</p>}

                    <div className={styles.buttons}>
                      <button
                        className={styles.signupButton}
                        type="submit"
                        disabled={loading}
                      >
                        {loading ? "Registering..." : "SIGN UP"}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  <div className={styles.welcomeMsg}>
                    <h1>Verify OTP</h1>
                    <p>Enter the OTPs sent to your Email and Mobile</p>
                  </div>

                  <form onSubmit={handleVerifyOtp}>
                    <div className={styles.inputs}>
                      {/* EMAIL OTP */}
                      <label className={styles.label}>Email OTP</label>
                      <div className={styles.otpBoxGroup}>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <input
                            key={i}
                            type="text"
                            maxLength="1"
                            value={otpData.email_otp[i] || ""}
                            onChange={(e) =>
                              handleOtpBoxChange(e, i, "email_otp")
                            }
                            onKeyDown={(e) =>
                              handleOtpBoxKeyDown(e, i, "email_otp")
                            }
                            className={styles.otpBox}
                            inputMode="numeric"
                          />
                        ))}
                      </div>

                      {/* MOBILE OTP */}
                      <label className={styles.label}>Mobile OTP</label>
                      <div className={styles.otpBoxGroup}>
                        {Array.from({ length: 6 }).map((_, i) => (
                          <input
                            key={i}
                            type="text"
                            maxLength="1"
                            value={otpData.mobile_otp[i] || ""}
                            onChange={(e) =>
                              handleOtpBoxChange(e, i, "mobile_otp")
                            }
                            onKeyDown={(e) =>
                              handleOtpBoxKeyDown(e, i, "mobile_otp")
                            }
                            className={styles.otpBox}
                            inputMode="numeric"
                          />
                        ))}
                      </div>
                    </div>

                    {error && <p className={styles.errorMessage}>⚠️ {error}</p>}

                    <div className={styles.buttons}>
                      <button
                        className={styles.signupButton}
                        type="submit"
                        disabled={verifying}
                      >
                        {verifying ? "Verifying..." : "VERIFY OTP"}
                      </button>
                    </div>
                  </form>
                </>
              )}

              <div className={styles.loginLink}>
                <p>
                  Have an account already?{" "}
                  <span className={styles.link} onClick={() => navigate("/")}>
                    Login
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT PART */}
          <div className={styles.rightPart}>
            <div className={styles.rightPartContent}>
              <div className={styles.carousel}>
                <div className={styles.dots}>
                  {images.map((_, index) => (
                    <span
                      key={index}
                      className={`${styles.dot} ${
                        currentImageIndex === index ? styles.active : ""
                      }`}
                      onClick={() => handleDotClick(index)}
                    />
                  ))}
                </div>
                <div
                  className={styles.carouselTrack}
                  style={{
                    transform: `translateX(-${currentImageIndex * 100}%)`,
                  }}
                >
                  {images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt="Car"
                      className={styles.carImage}
                      onError={(e) => {
                        // Fallback to default images if featured image fails to load
                        const defaultImages = [carImage1, carImage2, carImage2];
                        e.target.src = defaultImages[index % defaultImages.length];
                      }}
                    />
                  ))}
                </div>
                <div className={styles.carouselControls}>
                  <button
                    className={`${styles.carouselButton} ${styles.prev}`}
                    onClick={handlePrevImage}
                    type="button"
                    aria-label="Previous image"
                  >
                    <span>&lt;</span>
                  </button>
                  <button
                    className={`${styles.carouselButton} ${styles.next}`}
                    onClick={handleNextImage}
                    type="button"
                    aria-label="Next image"
                  >
                    <span>&gt;</span>
                  </button>
                </div>
                <div className={styles.imageContent}>
                  <h2>Cars In Auction Today</h2>
                  <p>Bid Bold. Drive Trusted</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
